<template>
  <div class="reference-login-page">
    <div class="reference-login-background" aria-hidden="true"></div>

    <div class="reference-login-overlay">
      <header class="reference-login-nav">
        <button type="button" class="reference-back-button" aria-label="Back" @click="back">
          <img :src="backIcon" alt="">
        </button>

        <div class="reference-language-control">
          <img :src="languageIcon" alt="Language">
          <ChangeLanguage />
        </div>
      </header>

      <main class="reference-login-content">
        <section class="reference-logo-box">
          <img class="reference-logo" :src="referenceLogo" alt="">
          <p>Login Member Window</p>
        </section>

        <form class="reference-login-form" @submit.prevent="doLogin">
          <van-field
            v-model="username"
            class="reference-input"
            type="text"
            input-align="center"
            autocomplete="username"
            :placeholder="$t('login.usernamePls')"
          />

          <van-field
            v-model="password"
            class="reference-input"
            :type="passwordType"
            input-align="center"
            autocomplete="current-password"
            :placeholder="$t('login.passwordPls')"
            :right-icon="passwordType === 'password' ? 'closed-eye' : 'eye-o'"
            @click-right-icon="switchPasswordType"
          />

          <van-button
            class="reference-login-button"
            native-type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            {{ $t('login.doLogin') }}
          </van-button>

          <div class="reference-register-row">
            <span aria-hidden="true"></span>
            <button type="button" @click="toRegister">
              {{ $t('login.toRegister') }}
            </button>
          </div>
        </form>
      </main>
    </div>
  </div>
</template>

<script>
import ChangeLanguage from '@/i18n/change-language'
import { persistLanguage, getStoredLanguage } from '@/i18n/language'
import { mapActions, mapGetters } from 'vuex'
import backIcon from '@/assets/img/reference-login/back.png'
import languageIcon from '@/assets/img/reference-login/language.png'
import referenceLogo from '@/assets/img/reference-login/crush-logo.png'

export default {
  name: 'ReferenceLogin',
  components: {
    ChangeLanguage
  },
  data() {
    return {
      username: '',
      password: '',
      passwordType: 'password',
      isLoading: false,
      backIcon,
      languageIcon,
      referenceLogo
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn'])
  },
  methods: {
    ...mapActions(['login']),
    switchPasswordType() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password'
    },
    back() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      }
    },
    toRegister() {
      if (this.$route.path !== '/Register') {
        this.$router.push('/Register')
      }
    },
    async doLogin() {
      if (this.isLoading) return
      if (!this.username.trim()) {
        this.$toast(this.$t('login.usernamePls'))
        return
      }
      if (!this.password.trim()) {
        this.$toast(this.$t('login.passwordPls'))
        return
      }

      const selectedLanguage = persistLanguage(this.$i18n.locale || getStoredLanguage())
      this.isLoading = true
      try {
        const result = await this.login({
          username: this.username.trim(),
          password: this.password.trim()
        })
        this.$i18n.locale = persistLanguage(selectedLanguage)
        this.$toast.success(result.msg)
        if (this.$route.path !== '/Mine') {
          this.$router.push('/Mine')
        }
      } catch (error) {
        this.$toast(error.message || this.$t('login.loginFailed'))
      } finally {
        this.isLoading = false
      }
    }
  },
  created() {
    if (this.isLoggedIn && this.$route.path === '/Login') {
      this.$router.push('/Mine')
    }
  }
}
</script>

<style lang="less" scoped>
@import "../../assets/css/basenew.css";

@keyframes reference-background-pan {
  0%, 100% { background-position-x: 0; }
  50% { background-position-x: 100%; }
}

.reference-login-page {
  position: absolute;
  inset: 0;
  z-index: 2;
  min-height: 100vh;
  overflow-x: hidden;
  color: #e6b98a;
  background: #1f1c1f;
}

.reference-login-background {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  animation: reference-background-pan 20s linear infinite;
  background: #7e5678 url('../../assets/img/reference-login/login.jpg') no-repeat 0 0;
  background-size: auto 100%;
}

.reference-login-overlay {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
  background: rgba(0, 0, 0, .51);
}

.reference-login-nav {
  position: relative;
  width: 100%;
  height: 13.333vw;
  border-bottom: 1px solid transparent;
  background: transparent;
}

.reference-back-button {
  position: absolute;
  top: 50%;
  left: 4vw;
  display: grid;
  width: 44PX;
  height: 44PX;
  padding: 0;
  place-items: center;
  border: 0;
  outline: 0;
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
}

.reference-back-button img {
  width: 20PX;
  height: 16PX;
  max-width: none;
  object-fit: contain;
}

.reference-language-control {
  position: absolute;
  top: 1.333vw;
  right: 5.333vw;
  width: 8.267vw;
  height: 8.267vw;
  cursor: pointer;
}

.reference-language-control > img {
  position: absolute;
  top: 15%;
  left: 15%;
  width: 70%;
  height: 70%;
  object-fit: contain;
  pointer-events: none;
}

.reference-language-control /deep/ .change-language {
  position: absolute;
  inset: 0;
}

.reference-language-control /deep/ .language-selector {
  width: 100% !important;
  min-width: 0 !important;
  height: 100% !important;
  padding: 0 !important;
  border: 0 !important;
  opacity: 0 !important;
}

.reference-login-content {
  width: 100%;
  padding-bottom: max(4vw, env(safe-area-inset-bottom));
}

.reference-logo-box {
  text-align: center;
}

.reference-logo {
  display: block;
  width: 222PX;
  height: 222PX;
  margin: 0 auto;
  object-fit: contain;
}

.reference-logo-box p {
  margin: 0;
  padding-top: 4vw;
  color: #ccc;
  font-size: 16PX;
  line-height: 1.3;
}

.reference-login-form {
  width: 100%;
  padding: 2.667vw 8vw;
}

.reference-login-form .reference-input {
  width: 100% !important;
  height: auto;
  margin-top: 5.333vw;
  padding: 3.467vw 2.667vw;
  border: .267vw solid #766350;
  border-radius: 1.333vw;
  color: #e6b98a;
  background: linear-gradient(120deg, #212121, #313131);
  box-sizing: border-box;
}

.reference-input /deep/ .van-field__control {
  color: #e6b98a;
  font-size: 4vw !important;
  line-height: 1.2;
}

.reference-input /deep/ .van-field__control::placeholder {
  color: #ccc;
  opacity: 1;
}

.reference-input /deep/ .van-field__right-icon,
.reference-input /deep/ .van-icon {
  color: #969799;
  font-size: 6.667vw;
  line-height: 1;
}

.reference-login-button {
  width: 100%;
  height: 10.667vw;
  min-height: 0;
  margin-top: 11.333vw;
  padding: 0;
  border: 0;
  border-radius: 6.667vw;
  color: #68302c;
  background: linear-gradient(#fde3ca, #e7b486);
  font-size: 4vw;
  font-weight: 700;
  box-shadow: none;
}

.reference-login-button::before {
  display: none;
}

.reference-login-button /deep/ .van-button__text {
  color: #68302c;
  font-size: 4vw;
  font-weight: 700;
}

.reference-register-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 2.667vw;
  color: #bfa77c;
  font-size: 3.333vw;
}

.reference-register-row button {
  padding: 0;
  border: 0;
  color: inherit;
  font: inherit;
  background: transparent;
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .reference-login-background {
    animation: none;
  }
}
</style>

<style>
body.auth-page-body .change-language-popover.van-popover,
body.auth-page-body .change-language-popover {
  border: 1px solid rgba(230, 185, 138, .72) !important;
  border-radius: 10px !important;
  background: #212121 !important;
}

body.auth-page-body .change-language-popover .van-popover__action {
  color: #e6b98a !important;
  background: #212121 !important;
}
</style>
